// src/Components/AIBrain.js
import { queryModel, streamModel } from "../API/openrouterClient";

export const AVAILABLE_CATEGORIES = [
  "mindset", "physics", "ai", "business", "selfimprovement", "speeches",
  "science", "psychology", "intelligence", "knowledge", "geopolitics", "tech"
];

const MEMORY_KEY = "vitstra_ai_memory";

function getMemory() {
  try { return JSON.parse(localStorage.getItem(MEMORY_KEY)) || {}; } catch { return {}; }
}
function saveMemory(obj) {
  try { localStorage.setItem(MEMORY_KEY, JSON.stringify(obj)); } catch {}
}

function pushToHistory(history, role, content) {
  const updated = [...history, { role, content }];
  return updated.slice(-4);
}

// --- SMART classifyIntent ---
async function classifyIntent(userInput, chatHistory = []) {
  const lowerInput = userInput.toLowerCase();

  // === STEP 1: LOCAL CUSTOMIZATION CHECK (STRICT) ===
  const isCustomizationIntent = localCustomizationCheck(lowerInput);
  if (isCustomizationIntent) {
    const cats = extractCategoriesFromText(lowerInput);
    if (cats) {
      console.log("LOCAL CUSTOMIZATION DETECTED:", cats);
      return { intent: "customize", categories: cats, isCustom: true };
    }
  }

  // === STEP 2: AI CLASSIFICATION (ONLY IF NOT CLEAR) ===
  const availableCats = AVAILABLE_CATEGORIES.map(c => c.toLowerCase()).join('", "');
  const classificationPrompt = `You are a classifier. Output ONLY valid JSON.

Available categories: "${availableCats}"

Classify ONLY if user wants to CHANGE their feed:
- "Show AI"
- "Only tech and business"
- "Reset feed"
→ {"intent": "customize", "categories": [...]}

Otherwise (questions, explanations, etc.):
→ {"intent": "general", "categories": null}

Input: "${userInput}"

Output ONLY JSON:`;

  try {
    const { text: raw } = await queryModel(classificationPrompt, chatHistory.slice(-2), "Neutral");

    const jsonMatch = raw.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return { intent: "general", categories: null };

    let jsonStr = jsonMatch[0]
      .replace(/([{,])\s*(\w+)\s*:/g, '$1"$2":')
      .replace(/:\s*([a-zA-Z][^,\]}]*)/g, ': "$1"')
      .replace(/,\s*([}\]])/g, '$1')
      .trim();

    const parsed = JSON.parse(jsonStr);

    if (parsed.intent === "customize" && Array.isArray(parsed.categories)) {
      const normalized = parsed.categories.map(c => c.toLowerCase());
      console.log("AI CLASSIFICATION: customize →", normalized);
      return { intent: "customize", categories: normalized, isCustom: true };
    }
  } catch (err) {
    console.warn("AI classification failed, using local only");
  }

  return { intent: "general", categories: null, isCustom: false };
}

// --- STRICT LOCAL CHECK ---
function localCustomizationCheck(text) {
  const patterns = [
    /\b(show|only|focus|prefer|want to see|set to|change to|hide|remove|turn off|reset|all)\b/i,
    /\b(customi[sz]e|filter|limit)\b/i,
    /^reset feed$/i,
  ];
  return patterns.some(p => p.test(text));
}

function extractCategoriesFromText(text) {
  const lower = text.toLowerCase();
  const detected = new Set();
  const cats = AVAILABLE_CATEGORIES.map(c => c.toLowerCase());

  cats.forEach(cat => {
    if (lower.includes(cat)) detected.add(cat);
  });

  if (/(reset|default|all)/i.test(lower)) {
    detected.clear();
    detected.add("all");
  }

  return detected.size > 0 ? Array.from(detected) : null;
}

// --- POST-STREAM CHECK ---
function detectCustomizationIntent(aiText, userText, available) {
  const lowerAI = (aiText || "").toLowerCase();
  const lowerUser = (userText || "").toLowerCase();

  if (localCustomizationCheck(lowerUser)) {
    return extractCategoriesFromText(lowerUser);
  }

  if (/customi[sz]ing|showing.*content|feed updated/i.test(lowerAI)) {
    return extractCategoriesFromText(lowerAI);
  }

  return null;
}

// --- MAIN BRAIN ---
export async function interpretUserQuery(
  inputText,
  available = AVAILABLE_CATEGORIES,
  chatHistory = [],
  userMood = "Neutral",
  onStreamChunk,
  signal
) {
  try {
    const text = inputText.trim();
    if (!text) return { action: "empty" };

    const memory = getMemory();

    // Name handling
    const nameMatch = text.match(/my name is\s+([a-z]+)/i);
    if (nameMatch) {
      const name = nameMatch[1].charAt(0).toUpperCase() + nameMatch[1].slice(1);
      memory.name = name;
      saveMemory(memory);
      return { action: "memory_update", response: `Got it, ${name}. Nice to meet you!` };
    }

    if (/what('?s| is)? my name/i.test(text)) {
      return {
        action: "memory_recall",
        response: memory.name ? `Your name is ${memory.name}.` : `You haven't told me your name yet.`
      };
    }

    // === INTENT CLASSIFICATION ===
    const classification = await classifyIntent(text, chatHistory);

    // === APPLY CUSTOMIZATION (but don't return yet) ===
    let customizationResult = null;
    if (classification.isCustom && classification.categories) {
      const cats = classification.categories.map(c => c.toLowerCase());
      const filtered = cats.filter(c => c !== "all");

      localStorage.setItem("vitstraCustoms", JSON.stringify(filtered));
      localStorage.setItem("vitstraCategory", JSON.stringify(cats));
      window.dispatchEvent(new CustomEvent("vitstra-update", { detail: { category: cats } }));

      customizationResult = {
        action: "show",
        categories: cats,
        response: cats.includes("all")
          ? "Feed reset — showing **all** content."
          : `Customized your feed to **${cats.join(", ")}**.`,
      };
    }

    // Local rules
    const localResult = localInterpretation(text.toLowerCase(), available);
    if (localResult) return localResult;

    // === GENERAL AI RESPONSE ===
    const updatedHistory = pushToHistory(chatHistory, "user", text);
    if (memory.name) {
      updatedHistory.unshift({ role: "system", content: `User's name: ${memory.name}` });
    }

    const systemPrompt = `You are **VitStra AI**, a concise, insightful guide.

Answer clearly. Use **bold** for key points. Keep under 300 words.

If asked to customize feed, say: "Customizing to **X**" — otherwise, just answer.`;

    let finalText = "";

    if (typeof onStreamChunk === "function") {
      const { text: streamedText } = await streamModel(
        text,
        updatedHistory,
        userMood,
        (chunk) => {
          if (signal?.aborted) return;
          onStreamChunk(chunk);
        },
        systemPrompt,
        signal
      );
      finalText = streamedText || "";
      console.log("STREAMED TEXT LENGTH:", finalText.length);
    } else {
      const { text: aiText } = await queryModel(text, updatedHistory, userMood, systemPrompt);
      finalText = aiText || "";
      console.log("QUERIED TEXT LENGTH:", finalText.length);
    }

    console.log("FINAL AI TEXT (first 100 chars):", finalText.substring(0, 100) + "...");

    // === FALLBACK: EMPTY RESPONSE PROTECTION ===
    if (!finalText.trim()) {
      console.warn("EMPTY AI RESPONSE — USING FALLBACK FOR QUERY:", text);
      if (classification.intent === "general") {
        finalText = `Hmm, that query got me stumped! Could you rephrase it? For example, try:  
**"Tell me about the Winklevoss twins and Facebook"**  
Or customize your feed with **"Show AI"**. What else can I help with?`;
      } else {
        finalText = "Sorry, something went wrong. Try rephrasing or say **'Reset feed'**.";
      }
    }

    // === POST-STREAM CUSTOMIZATION DETECTION ===
    const postIntent = detectCustomizationIntent(finalText, text, available);
    if (postIntent) {
      const cats = postIntent.map(c => c.toLowerCase());
      const filtered = cats.filter(c => c !== "all");
      localStorage.setItem("vitstraCustoms", JSON.stringify(filtered));
      localStorage.setItem("vitstraCategory", JSON.stringify(cats));
      window.dispatchEvent(new CustomEvent("vitstra-update", { detail: { category: cats } }));
      return {
        action: "show",
        categories: cats,
        response: cats.includes("all")
          ? "Feed reset — showing **all** content."
          : `Customized your feed to **${cats.join(", ")}**.`,
      };
    }

    // === FINAL RETURN: Combine customization + AI response ===
    if (customizationResult) {
      return {
        ...customizationResult,
        response: customizationResult.response + "\n\n" + finalText.trim(),
      };
    }

    return {
      action: "ai",
      response: `${finalText}\n\n(Model: ${typeof onStreamChunk === "function" ? "streamed" : "queried"})`
    };

  } catch (err) {
    console.error("interpretUserQuery error:", err);
    return { action: "error", response: "Sorry, try again." };
  }
}

// --- Local Rules ---
function localInterpretation(text, available) {
  if (/who\s.*you|what.*vitstra|introduce|yourself/i.test(text)) {
    return {
      action: "about",
      response: `I'm **VitStra AI** — your guide to growth, science, and wisdom.

Try:  
**"Show AI"** → customize feed  
**"Explain quantum physics"** → deep answer`,
    };
  }

  if (/what.*can.*do|help|features/i.test(text)) {
    return {
      action: "info",
      response: `I can:  
• Answer any question  
• **Customize your feed**: say "Show AI" or "Only tech"  
• Remember things you say`,
    };
  }

  if (/^(hi|hello|hey)/i.test(text)) {
    return { action: "greeting", response: "Hey! What do you want to explore?" };
  }

  if (/^reset feed$/i.test(text)) {
    return {
      action: "reset",
      response: "Feed reset — showing all.",
      categories: ["all"],
    };
  }

  return null;
}